import { CdkDragEnd, CdkDragMove, CdkDragStart, DragDropModule } from '@angular/cdk/drag-drop';
import { OverlayRef } from '@angular/cdk/overlay';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  OnInit,
  Optional,
  Output,
  Renderer2,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DeviceService } from '@shared/services';
import { IBottomSheetHeighOption, IconTypes, KeyboardService, SdBottomSheetOptions } from '@shared/v2';
import { WindowRef } from '@spartacus/core';
import { filter, fromEvent, map, tap, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { SdvorClickOutsideDirective } from '../../directives/';
import { SdvorIconComponent } from '../icon/icon.component';
import { SD_BOTTOM_SHEET_OPTIONS_TOKEN } from './bottom-sheet.token';

const KEYBOARD_CLOSE_COOLDOWN_MS = 600;
const COLLAPSE_TOLERANCE_PX = 1;

@Component({
  selector: 'sd-bottom-sheet',
  imports: [SdvorIconComponent, SdvorClickOutsideDirective, DragDropModule],
  templateUrl: './bottom-sheet.component.html',
  styleUrls: ['./bottom-sheet.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SdBottomSheetComponent<T> implements OnInit, AfterViewInit {
  readonly iconTypes = IconTypes;

  sheetHeight = signal<number>(0);
  showKeyboard = signal<boolean>(false);
  isDragging = signal<boolean>(false);
  readonly isBackdropVisible = computed(() => {
    const currentHeight = this.sheetHeight();
    if (currentHeight <= 0) {
      return false;
    }
    if (this.opt.overlayDismissMode === 'collapse') {
      return currentHeight > this.calculateHeight(this.opt.init) + COLLAPSE_TOLERANCE_PX;
    }

    return true;
  });
  dragStartHeight = 0;
  private lastKeyboardCloseTime = 0;
  private hadKeyboardOpenInCollapseMode = false;

  @Output() close$ = new EventEmitter<void>();

  @ViewChild('draggableItem') draggableItem!: ElementRef;
  @ViewChild('dragHandle', { read: ElementRef }) dragHandle!: ElementRef;

  constructor(
    private winRef: WindowRef,
    private renderer: Renderer2,
    private destroyRef: DestroyRef,
    private deviceService: DeviceService,
    private keyboardService: KeyboardService,
    @Inject(SD_BOTTOM_SHEET_OPTIONS_TOKEN) public opt: SdBottomSheetOptions<T>,
    @Inject(OverlayRef) @Optional() private overlayRef?: OverlayRef,
  ) {}

  get nativeDraggableElement(): HTMLElement | null {
    return this.draggableItem?.nativeElement || null;
  }

  get nativeWindow(): Window | undefined {
    return this.winRef.nativeWindow;
  }

  ngOnInit(): void {
    if (this.winRef.isBrowser()) {
      this.initialSheet();
    }
  }

  ngAfterViewInit(): void {
    if (this.winRef.isBrowser()) {
      this.setAccessibilityAttributes();
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  // eslint-disable-next-line no-unused-vars
  onEscapeKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Escape') {
      return;
    }
    this.onClickOutside();
  }

  calculateHeight(config?: IBottomSheetHeighOption): number {
    if (!config) return 0;

    return config.type === 'px' ? config.value : this.percentsToPixels(config.value);
  }

  initialSheet(): void {
    const { init, minHeight, closeDesktopDown, closeTabletDown } = this.opt;

    const sheetHeightInitPx = this.calculateHeight(init);
    const sheetMinHeightPercent = this.pixelsToPercents(this.calculateHeight(minHeight));

    this.sheetHeight.set(sheetHeightInitPx);
    this.opt.breakpoints = [sheetMinHeightPercent, ...this.opt.breakpoints];

    const shouldClose = (isDown: boolean): void => {
      if (isDown) {
        this.onClose();
      }
    };

    const closeObservable$ = closeDesktopDown
      ? this.deviceService.isDesktopLgUp$
      : closeTabletDown === undefined || closeTabletDown
        ? this.deviceService.isTabletMdUp$
        : null;

    closeObservable$?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(shouldClose);

    this.keyboardService.keyboardState
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((isKeyboardOpen) => {
          this.showKeyboard.set(isKeyboardOpen);
          if (!isKeyboardOpen) {
            this.lastKeyboardCloseTime = Date.now();
          }
        }),
        switchMap((isKeyboardOpen) => timer(KEYBOARD_CLOSE_COOLDOWN_MS).pipe(map(() => isKeyboardOpen))),
        filter((isKeyboardOpen) => {
          if (isKeyboardOpen && Date.now() - this.lastKeyboardCloseTime < KEYBOARD_CLOSE_COOLDOWN_MS) {
            return false;
          }

          return true;
        }),
        tap((isKeyboardOpen) => {
          if (this.opt.overlayDismissMode === 'collapse') {
            if (isKeyboardOpen) {
              this.hadKeyboardOpenInCollapseMode = true;
              this.expandToMaxHeight(true);
            } else if (this.hadKeyboardOpenInCollapseMode) {
              this.expandToMaxHeight(false);
            }

            return;
          }
          this.expandToMaxHeight(isKeyboardOpen);
        }),
      )
      .subscribe();
  }

  private expandToMaxHeight(expandForKeyboard: boolean): void {
    if (!this.nativeDraggableElement) {
      return;
    }
    const sortedBreakpoints = [...this.opt.breakpoints].sort((a, b) => a - b);
    const maxBp = sortedBreakpoints[sortedBreakpoints.length - 1];
    const targetPercent = expandForKeyboard ? Math.max(maxBp, 90) : maxBp;
    const targetHeight = this.percentsToPixels(targetPercent);
    this.renderer.addClass(this.nativeDraggableElement, 'smooth-transition');
    this.sheetHeight.set(targetHeight);
  }

  onClickOutside(): void {
    if (this.opt.ignoreOutsideClick) {
      return;
    }
    if (this.opt.overlayDismissMode === 'collapse') {
      if (this.isBackdropVisible()) {
        this.collapseToInitialHeight();
      }

      return;
    }
    this.onClose();
  }

  onBackdropClick(event: Event): void {
    event.stopPropagation();
    this.onClickOutside();
  }

  onClose(): void {
    this.opt.onClose?.();
    this.close$.emit();
    if (this.overlayRef) {
      this.overlayRef.detach();
    }
  }

  private collapseToInitialHeight(): void {
    if (!this.nativeDraggableElement) {
      return;
    }

    const targetHeight = this.calculateHeight(this.opt.init);
    this.renderer.addClass(this.nativeDraggableElement, 'smooth-transition');
    this.sheetHeight.set(targetHeight);
  }

  private setAccessibilityAttributes(): void {
    if (this.nativeDraggableElement) {
      this.renderer.setAttribute(this.nativeDraggableElement, 'role', 'dialog');
      this.renderer.setAttribute(this.nativeDraggableElement, 'aria-modal', 'true');
      if (this.opt.title) {
        this.renderer.setAttribute(this.nativeDraggableElement, 'aria-labelledby', 'bottom-sheet-title');
      }
    }
  }

  onDragStarted(event: CdkDragStart): void {
    if (!this.winRef.isBrowser() || !this.nativeDraggableElement || this.showKeyboard()) {
      event.source.reset();

      return;
    }

    this.isDragging.set(true);
    this.dragStartHeight = this.sheetHeight();

    const element = event.source.element.nativeElement;
    if (element) {
      this.renderer.setStyle(element, 'transform', 'none');
    }
  }

  onDragMoved(event: CdkDragMove): void {
    if (!this.winRef.isBrowser() || !this.nativeDraggableElement || this.showKeyboard()) {
      return;
    }

    const deltaY = event.delta.y;
    const newHeight = this.dragStartHeight - deltaY;
    const minHeight = this.calculateHeight(this.opt.minHeight);
    const maxHeight = this.nativeWindow?.innerHeight || 0;

    const clampedHeight = Math.max(minHeight, Math.min(newHeight, maxHeight));
    this.sheetHeight.set(clampedHeight);

    const element = event.source.element.nativeElement;
    if (element) {
      this.renderer.setStyle(element, 'transform', 'none');
    }
  }

  onDragEnded(event: CdkDragEnd): void {
    if (!this.winRef.isBrowser() || !this.nativeDraggableElement) {
      return;
    }

    this.isDragging.set(false);

    const element = event.source.element.nativeElement;
    if (element) {
      this.renderer.setStyle(element, 'transform', 'none');
    }

    if (this.showKeyboard()) {
      return;
    }

    const currentHeightPercent = this.pixelsToPercents(this.sheetHeight());
    const sortedBreakpoints = [...this.opt.breakpoints].sort((a, b) => a - b);
    const minBreakpoint = sortedBreakpoints[0];
    const maxBreakpoint = sortedBreakpoints[sortedBreakpoints.length - 1];

    const deltaY = event.distance.y;
    const isSlideUp = deltaY < 0;

    if (Math.abs(deltaY) < 10) {
      const nearestBreakpoint = sortedBreakpoints.reduce((prev, curr) => {
        const prevDiff = Math.abs(currentHeightPercent - prev);
        const currDiff = Math.abs(currentHeightPercent - curr);

        return currDiff < prevDiff ? curr : prev;
      });
      this.sheetHeight.set(this.percentsToPixels(nearestBreakpoint));

      return;
    }

    const canCloseCompletely = minBreakpoint === 0;

    if (!isSlideUp && canCloseCompletely) {
      const nextBreakpoint = sortedBreakpoints[1] ?? minBreakpoint;
      if (currentHeightPercent <= nextBreakpoint) {
        this.sheetHeight.set(0);
        this.renderer.setStyle(this.draggableItem.nativeElement, 'padding', '0');

        fromEvent(this.draggableItem.nativeElement, 'transitionend')
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            filter(() => this.sheetHeight() === 0),
          )
          .subscribe(() => {
            this.onClose();
          });

        return;
      }
    }

    if (!isSlideUp && !canCloseCompletely && currentHeightPercent <= minBreakpoint) {
      this.sheetHeight.set(this.percentsToPixels(minBreakpoint));

      return;
    }

    let targetBreakpoint: number;

    if (isSlideUp) {
      targetBreakpoint = sortedBreakpoints.find((bp) => bp > currentHeightPercent) ?? maxBreakpoint;
    } else {
      const lowerBreakpoints = [...sortedBreakpoints].reverse();
      targetBreakpoint = lowerBreakpoints.find((bp) => bp < currentHeightPercent) ?? minBreakpoint;
    }

    this.sheetHeight.set(this.percentsToPixels(targetBreakpoint));
  }

  private pixelsToPercents(blockHeight: number): number {
    const windowHeight = this.nativeWindow?.innerHeight || 0;
    if (windowHeight <= 0 || blockHeight <= 0) {
      return 0;
    }

    return Math.abs((blockHeight * 100) / windowHeight);
  }

  private percentsToPixels(percentHeight: number): number {
    const windowHeight = this.nativeWindow?.innerHeight || 0;
    if (windowHeight <= 0 || percentHeight < 0) {
      return 0;
    }

    return Math.trunc((percentHeight / 100) * windowHeight);
  }
}
